import { Controller } from "@hotwired/stimulus"
import { MarkerClusterer } from "@googlemaps/markerclusterer"
import { renderMobileStoreCard } from "../modules/map_store_card.mjs"

// map view stores main
export default class extends Controller {
  static targets = ["canvas", "storeCard"]
  static values = { zoom: Number }

  connect() {
    this.markers = []

    // Listener para Turbo Stream sobre #stores-list. Hay dos flujos:
    //  - append (paginación "Mostrar más"): solo agrega los pines nuevos.
    //  - update/replace (búsqueda/filtro en vivo): la lista se REEMPLAZA, así que
    //    re-sincronizamos TODOS los pines con el nuevo set (antes quedaban viejos:
    //    buscabas "verde fresco", la lista dejaba 1 pero el mapa seguía con 3).
    this._onTurboStream = (event) => {
      const target = event.target
      if (target.target !== "stores-list") return

      if (target.action === "append") {
        setTimeout(() => this.appendNewMarkers(), 100)
      } else if (target.action === "update" || target.action === "replace") {
        setTimeout(() => {
          this.hideStoreCard() // por si quedó abierta una card de un resultado que ya no está
          this.refreshAllMarkers(true) // fit: vuela el mapa al nuevo resultado
        }, 100)
      }
    }
    document.addEventListener("turbo:before-stream-render", this._onTurboStream)

    // Apertura del mapa. El overlay (this.element = #collapseMap) ya NO es un collapse
    // de Bootstrap: lo abrimos/cerramos con la clase .is-open y, en mobile, es un
    // position:fixed que cubre el viewport (dimensionado por CSS → independiente del
    // scroll). Los botones que abren viven FUERA del overlay, así que los cableamos
    // por selector; el botón nativo hace element.click() y cae en el mismo handler.
    this.isOpen = false
    this.searchHeaderEl = this.element.previousElementSibling // para medir el top al abrir
    this._onToggle = (e) => { e.preventDefault(); this.toggle() }
    this.toggleButtons = Array.from(document.querySelectorAll("[data-map-toggle]"))
    this.toggleButtons.forEach(b => b.addEventListener("click", this._onToggle))

    // Esperar a que Google Maps esté listo
    if (this.isGoogleMapsReady()) {
      this.createMap()
    } else {
      this.waitForGoogleMaps()
    }
  }

  isGoogleMapsReady() {
    return typeof google !== "undefined" &&
           google.maps &&
           google.maps.Map &&
           google.maps.Marker &&
           google.maps.InfoWindow
  }

  waitForGoogleMaps() {
    const checkInterval = setInterval(() => {
      if (this.isGoogleMapsReady()) {
        clearInterval(checkInterval)
        this.createMap()
      }
    }, 100)

    // Timeout after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval)
      if (!this.isGoogleMapsReady()) {
        console.error("Google Maps no se pudo cargar")
      }
    }, 10000)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this._onTurboStream)
    this.toggleButtons?.forEach(b => b.removeEventListener("click", this._onToggle))
    this.unlockBodyScroll() // por si se navega con el overlay abierto
  }

  /* ----------------------------- Abrir / cerrar ----------------------------- */
  toggle() { this.isOpen ? this.close() : this.open() }

  open() {
    this.isOpen = true

    if (this.isMobile()) {
      // Subimos al tope ANTES de abrir: así el header de búsqueda queda en su sitio y
      // el overlay (fixed) arranca justo debajo, sin importar dónde estaba el scroll.
      this._scrollY = window.scrollY
      window.scrollTo(0, 0)
      // top del overlay = fondo del header de búsqueda, medido UNA vez a scroll 0
      // (confiable). Sirve para web y native sin números mágicos por contexto.
      const top = this.searchHeaderEl
        ? Math.max(0, Math.round(this.searchHeaderEl.getBoundingClientRect().bottom))
        : 0
      this.element.style.setProperty("--map-top", `${top}px`)
      this.lockBodyScroll()
    }

    this.element.classList.add("is-open")
    this.toggleButtons.forEach(b => b.setAttribute("aria-expanded", "true"))

    // Google Maps quedó en display:none al renderizar → al mostrarlo hay que avisarle
    // que recalcule tamaño. PRESERVAMOS el centro actual (no el default): el resize
    // puede recolocar, así que re-asentamos el MISMO centro. Sin esto, al reabrir se
    // perdía la vista previa (ej. buscaste Buzios, cerraste y reabrías → volvía al
    // default en vez de quedarse en Buzios). Si hay un fit pendiente, ese manda.
    requestAnimationFrame(() => {
      if (!this.map) return
      const center = this.map.getCenter()
      google.maps.event.trigger(this.map, "resize")
      if (center) this.map.setCenter(center)
      if (this._pendingFit) this.fitBoundsToMarkers()
    })
  }

  close() {
    this.isOpen = false
    this.element.classList.remove("is-open")
    this.toggleButtons.forEach(b => b.setAttribute("aria-expanded", "false"))
    this.hideStoreCard()
    this.unlockBodyScroll()
  }

  // Lock real de iOS: position:fixed (overflow:hidden solo no basta). Guardamos el
  // scroll previo de la lista para restaurarlo al cerrar.
  lockBodyScroll() {
    if (this._bodyLocked) return
    this._bodyLocked = true
    const s = document.body.style
    s.position = "fixed"; s.top = "0"; s.left = "0"; s.right = "0"; s.width = "100%"
  }

  unlockBodyScroll() {
    if (!this._bodyLocked) return
    this._bodyLocked = false
    const s = document.body.style
    s.position = ""; s.top = ""; s.left = ""; s.right = ""; s.width = ""
    window.scrollTo(0, this._scrollY || 0)
  }

  readCoordsFromCookie() {
    const raw = document.cookie.split("; ").find(r => r.startsWith("last_location="))
    if (!raw) return null
    const [lat, lon] = decodeURIComponent(raw.split("=")[1]).split("&").map(Number)
    return { lat, lon }
  }

  createMap() {
    const stored = this.readCoordsFromCookie()
    const fallback = { lat: -33.45, lng: -70.65 }
    const center = stored ? { lat: stored.lat, lng: stored.lon } : fallback

    this.map = new google.maps.Map(this.canvasTarget, {
      center,
      zoom: this.zoomValue || 13,
      styles: this.styles(),
      streetViewControl: false,
      zoomControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      rotateControl: false,
      cameraControl: false,
      gestureHandling: 'greedy'
    })
  
    if (stored) this.addOrUpdateUserMarker(center)

    // Tocar el mapa (fuera de un pin) cierra la tarjeta de tienda en mobile.
    this.map.addListener("click", () => this.hideStoreCard())

    this.refreshAllMarkers()
  }

  addOrUpdateUserMarker(pos) {
    if (this.userMarker) this.userMarker.setMap(null)
    this.userMarker = new google.maps.Marker({
      map: this.map,
      position: pos,
      title: "Tu ubicación",
      icon: "/user-position.png"
    })
  }

  refreshAllMarkers(fit = false) {
    if (!this.map) return

    const storeElems = Array.from(document.querySelectorAll(".store-data"))
    const stores = storeElems.map(el => this.storeDataFromElement(el))
    this.renderAllMarkers(stores)
    if (fit) this.fitBoundsToMarkers()
  }

  // Vuela el mapa para encuadrar los pines actuales (tras una búsqueda/filtro). Si el
  // mapa está cerrado (sin tamaño) lo deja pendiente y se aplica al abrir (open()).
  fitBoundsToMarkers() {
    if (!this.map || !this.markers?.length) return

    if (this.canvasTarget.offsetParent === null) { // overlay cerrado → sin tamaño
      this._pendingFit = true
      return
    }
    this._pendingFit = false

    const bounds = new google.maps.LatLngBounds()
    this.markers.forEach(m => bounds.extend(m.getPosition()))

    // Incluir TU ubicación en el encuadre SOLO si está cerca del cluster (~16 km),
    // para que al volver a las cercanas te veas en el mapa. Si la tienda está lejos
    // (buscar otra ciudad/país) NO te incluimos: queremos volar a la tienda, no
    // encuadrar medio continente.
    const userPos = this.userMarker?.getPosition()
    if (userPos) {
      const m = 0.15 // grados ≈ 16 km de margen
      const sw = bounds.getSouthWest()
      const ne = bounds.getNorthEast()
      const near = userPos.lat() > sw.lat() - m && userPos.lat() < ne.lat() + m &&
                   userPos.lng() > sw.lng() - m && userPos.lng() < ne.lng() + m
      if (near) bounds.extend(userPos)
    }

    // maxZoom: con 1 sola tienda (o tienda + usuario muy juntos) fitBounds acercaría
    // DE MÁS (quedaba demasiado zoom y no se veía tu punto); lo limitamos a 15 para
    // dejar contexto alrededor.
    google.maps.event.addListenerOnce(this.map, "idle", () => {
      if (this.map.getZoom() > 15) this.map.setZoom(15)
    })
    // Padding: abajo deja aire para la card; arriba bajo el search.
    this.map.fitBounds(bounds, { top: 80, right: 56, bottom: 150, left: 56 })
  }

  appendNewMarkers() {
    if (!this.map) return

    const newElems = Array.from(document.querySelectorAll(".store-data.new"))
    const newStores = newElems.map(el => this.storeDataFromElement(el))

    this.renderMarkers(newStores)
    newElems.forEach(el => el.classList.remove("new"))
  }
  

  renderAllMarkers(stores) {
    this.markers?.forEach(m => m.setMap(null))
    this.markers = []
    this.renderMarkers(stores)
  }

  renderMarkers(stores) {
    const info = new google.maps.InfoWindow()

    // Close button listener
    info.addListener("domready", () => {
      const closeBtn = document.querySelector(".map-popup-close")
      if (closeBtn) {
        closeBtn.onclick = () => info.close()
      }
    })

    stores.forEach(s => {
      const markerOptions = {
        map: this.map,
        position: { lat: s.lat, lng: s.lng },
        title: s.name,
        icon: "/marker.png"
      }

      const marker = new google.maps.Marker(markerOptions)

      marker.addListener("click", () => {
        // Mobile: tarjeta propia anclada abajo (estilo app). Desktop: InfoWindow sobre el pin.
        if (this.isMobile()) {
          info.close()
          this.showStoreCard(s)
        } else {
          info.setContent(this.cardHTML(s))
          info.open(this.map, marker)
        }
      })

      this.markers.push(marker)
    })

    this.clusterer?.clearMarkers()
    this.clusterer = new MarkerClusterer({
      map: this.map,
      markers: this.markers,
      renderer: {
        render: ({ count, position }) =>
          new google.maps.Marker({
            position,
            icon: {
              url: "/36-36.png",
              scaledSize: new google.maps.Size(36, 36),
              anchor: new google.maps.Point(18, 18)
            },
            label: { text: String(count), color: "#fff", fontSize: "12px" },
            zIndex: 1000 + count
          })
      }
    })
  }

  storeDataFromElement(el) {
    return {
      lat: Number(el.dataset.latitude),
      lng: Number(el.dataset.longitude),
      name: el.dataset.name,
      description: el.dataset.description,
      headerUrl: el.dataset.headerurl,
      link: el.dataset.link,
      distance: el.dataset.distance,
      delivery: el.dataset.delivery === "true",
      closed: el.dataset.closed === "true",
      openLabel: el.dataset.openLabel,
      discount: Number(el.dataset.discount || 0)
    }
  }

  isMobile() {
    return window.matchMedia("(max-width: 991px)").matches
  }

  // Escapa texto del partner (nombre/descripción) antes de insertarlo como innerHTML.
  esc(str) {
    const d = document.createElement("div")
    d.textContent = str ?? ""
    return d.innerHTML
  }

  // Tarjeta de tienda anclada abajo (mobile): la rellenamos y la deslizamos hacia
  // arriba. Reemplaza al InfoWindow en mobile. Tocar el mapa o la X la cierra.
  showStoreCard(s) {
    if (!this.hasStoreCardTarget) return
    const card = this.storeCardTarget

    // Store data belongs to a partner. Keep it as DOM data so quotes or tags in
    // a name/description can never become markup when the mobile card opens.
    renderMobileStoreCard({
      document,
      card,
      store: s
    })

    requestAnimationFrame(() => card.classList.add("is-visible"))
  }

  hideStoreCard(event) {
    event?.preventDefault()
    event?.stopPropagation()
    if (this.hasStoreCardTarget) this.storeCardTarget.classList.remove("is-visible")
  }

  cardHTML(s) {
    // Mismo criterio que la card mobile: cerrada → sin descuento (no está
    // disponible) y mostramos cuándo abre (helper next_open_time).
    const discountBadge = s.discount > 0 && !s.closed
      ? `<span class="map-popup-discount">${s.discount}% dscto</span>`
      : ""
    const closed = s.closed && s.openLabel
      ? `<p class="map-popup-closed"><i class="fas fa-clock"></i> ${s.openLabel}</p>`
      : ""
    const desc = s.description
      ? `<p class="map-popup-desc">${this.esc(s.description)}</p>` : ""

    return `
      <div class="map-popup">
        <button class="map-popup-close" type="button">&times;</button>
        <a href="${s.link}" class="map-popup-image">
          <img src="${s.headerUrl}" alt="${this.esc(s.name)}">
          ${discountBadge}
        </a>
        <h4 class="map-popup-name">${this.esc(s.name)}</h4>
        ${desc}
        ${closed}
      </div>`
  }

  styles() {
    return [{
      "featureType": "administrative.country",
      "elementType": "all",
      "stylers": [{ "visibility": "on" }]
    }]
  }
}
